import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { GiftEngine, giftLostReason, refreshCartUI } from '@theme/gift-engine';
import { debounce } from '@theme/utilities';

/**
 * BOXR gift reconcile (BOX-318).
 *
 * Keeps gift lines honest after the customer edits the cart. The element is only
 * rendered while the cart actually holds a gift group, and the cart drawer re-renders on
 * every change, so mounting is the signal: whenever the cart changed and gifts are in
 * play, this runs.
 *
 * It corrects two things the quantity steppers would otherwise leave behind: a gift
 * whose qualifying pack is gone, and a gift that is no longer discounted because the
 * campaign ended while the cart sat there.
 */

/** How long to wait before taking the one second look described in #run. */
const RECHECK_MS = 2500;

/**
 * How long the cart has to sit still before anything is corrected. Writing while the
 * customer is still clicking changes the line keys their next click is aimed at, and
 * Shopify then refuses that click with "no valid id or line parameter" — the change is
 * lost and the cart is left wrong. Waiting out the burst also means the gift is written
 * once instead of after every step.
 */
const SETTLE_MS = 400;

let running = false;

/** A cart change that arrived while a run was busy, and still has to be judged. */
let pending = false;

/** Whether the second look in #run was already taken, so it stays a page-load thing. */
let checkedOnLoad = false;

export class GiftReconcile extends Component {
  #abortController = new AbortController();

  /** Waits for the customer to stop clicking before touching the cart. */
  #runWhenSettled = debounce(() => this.#run(), SETTLE_MS);

  connectedCallback() {
    super.connectedCallback();

    // A morph can take this element out and put it back. The old controller is aborted
    // by then, and a listener registered on it would never fire again.
    this.#abortController = new AbortController();
    this.#tellSoldOutApart();
    this.#restartOnClick();
    this.#run(!checkedOnLoad);

    document.addEventListener(ThemeEvents.cartUpdate, this.#runWhenSettled, {
      signal: this.#abortController.signal,
    });

    // The cart is redrawn after this element mounts, so the wording is corrected again
    // once the new markup has landed.
    document.addEventListener(ThemeEvents.cartUpdate, () => queueMicrotask(() => this.#tellSoldOutApart()), {
      signal: this.#abortController.signal,
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#tellSoldOutApart();
  }

  /** The pack leaves the cart first, or adding it again on the product page leaves two. */
  #restartOnClick() {
    document.addEventListener(
      'click',
      async (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const link = target.closest('[data-gift-restart]');
        if (!(link instanceof HTMLAnchorElement)) return;

        event.preventDefault();
        link.setAttribute('aria-disabled', 'true');

        await GiftEngine.dropGroup(link.dataset.giftRestart ?? '');
        window.location.href = link.href;
      },
      { signal: this.#abortController.signal }
    );
  }

  /**
   * A gift that sold out reads as a gift the customer threw away, because the cart draws
   * the same line for both. The engine notes which groups lost theirs, so the wording is
   * corrected here rather than carried through the cart.
   */
  #tellSoldOutApart() {
    for (const reminder of document.querySelectorAll('.gift-reminder[data-gift-group]')) {
      if (!(reminder instanceof HTMLElement)) continue;

      const reason = giftLostReason(reminder.dataset.giftGroup);
      if (!reason) continue;

      const replacement =
        reason === 'sold-out' ? reminder.dataset.soldOutText : reminder.dataset.notDiscountedText;

      const text = reminder.querySelector('.gift-reminder__text');
      if (text instanceof HTMLElement && replacement) text.textContent = replacement;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // The cart re-renders on every change, so without this the page collects one live
    // listener per cart edit.
    this.#abortController.abort();
    this.#runWhenSettled.cancel();
  }

  /**
   * @param {boolean} [recheck] Whether to take a second look when nothing changed
   */
  async #run(recheck = false) {
    // Reconciling changes the cart, which re-renders the drawer and mounts a fresh copy
    // of this element. The flag keeps that from turning into a loop. What arrives while
    // a run is busy is remembered rather than dropped: a customer clicking the stepper
    // quickly would otherwise leave the last change unjudged, and the cart stays wrong
    // until the page is loaded again.
    if (running) {
      pending = true;
      return;
    }

    running = true;

    let changed = 0;
    try {
      changed = await GiftEngine.reconcile();
      if (changed > 0) await refreshCartUI('gift-reconcile', false);
    } finally {
      running = false;
    }

    if (pending) {
      pending = false;
      this.#runWhenSettled();
      return;
    }

    checkedOnLoad = true;

    // The very first cart read after a page load can still report the discount the cart
    // had before the campaign was switched off, and then there is nothing to correct
    // yet. A plain page view dispatches no cart update, so without this one second look
    // the stale gift would sit there until the customer's next visit.
    if (!recheck || changed > 0) return;

    const timer = setTimeout(() => this.#run(), RECHECK_MS);
    this.#abortController.signal.addEventListener('abort', () => clearTimeout(timer));
  }
}

if (!customElements.get('gift-reconcile-component')) {
  customElements.define('gift-reconcile-component', GiftReconcile);
}
